import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import File from '~/app/modules/form/File';
import classes from './style.module.scss';
import { useStoreState } from '~/store';
import {
  MAX_ISSUE_FILES,
  MAX_ISSUE_FILE_SIZE_MB,
  MAX_ISSUE_IMAGES,
  MAX_ISSUE_IMAGE_SIZE_MB,
} from '~/app/data/statuses/issue';

type AttachmentItem = string | File;

interface AttachmentsProps {
  onImagesChange: (files: AttachmentItem[]) => void;
  onFilesChange: (files: AttachmentItem[]) => void;
  defaultImages?: AttachmentItem[];
  defaultFiles?: AttachmentItem[];
}

export type AttachmentsComponent = React.FC<AttachmentsProps>;

const isFileItem = (item: AttachmentItem): item is File => typeof item !== 'string';

const getFileKey = (file: File): string => `${file.name}-${file.size}`;

const getExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() || '').toLowerCase() : '';
};

const getDisplayName = (item: AttachmentItem): string => {
  if (isFileItem(item)) {
    return item.name;
  }
  const rawPath = item.split('?')[0];
  const filename = rawPath.substring(rawPath.lastIndexOf('/') + 1) || item;
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

const Attachments: AttachmentsComponent = ({
  onImagesChange,
  onFilesChange,
  defaultImages = [],
  defaultFiles = [],
}) => {
  const { t } = useTranslation('issue');
  const allowedFileExtensions = useStoreState(state => state.machines.allowedIssueFileExtensions);

  const [images, setImages] = useState<AttachmentItem[]>(defaultImages);
  const [files, setFiles] = useState<AttachmentItem[]>(defaultFiles);

  const imageInputRef = useRef<any>(null);
  const fileInputRef = useRef<any>(null);

  const [isExpanded, setIsExpanded] = useState(true);
  const toggleExpand = () => {
    setIsExpanded(prevState => !prevState);
  };

  const allowedExtensionsNormalized = useMemo(
    () => (allowedFileExtensions || []).map(ext => ext.replace(/^\./, '').toLowerCase()).filter(Boolean),
    [allowedFileExtensions]
  );

  const fileAccept = useMemo(() => {
    if (!allowedExtensionsNormalized.length) {
      return undefined;
    }
    return allowedExtensionsNormalized.map(ext => `.${ext}`).join(',');
  }, [allowedExtensionsNormalized]);

  const rejectAndRemove = useCallback((ref: React.MutableRefObject<any>, index: number, message: string) => {
    ref.current?.handleImageDelete(index);
    toast.error(message);
  }, []);

  const findDuplicateFileIndex = useCallback((list: AttachmentItem[]): number => {
    const seen = new Set<string>();
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (!isFileItem(item)) {
        continue;
      }
      const key = getFileKey(item);
      if (seen.has(key)) {
        return i;
      }
      seen.add(key);
    }
    return -1;
  }, []);

  const handleImageChange = useCallback((e: any) => {
    const newFiles: AttachmentItem[] = e.target.value || [];
    const maxImageSizeBytes = MAX_ISSUE_IMAGE_SIZE_MB * 1024 * 1024;
    const oversizedIndex = newFiles.findIndex(f => isFileItem(f) && f.size > maxImageSizeBytes);
    if (oversizedIndex !== -1) {
      rejectAndRemove(imageInputRef, oversizedIndex, t('attachments.sizeError', { max: MAX_ISSUE_IMAGE_SIZE_MB }));
      return;
    }
    const duplicateIndex = findDuplicateFileIndex(newFiles);
    if (duplicateIndex !== -1) {
      rejectAndRemove(imageInputRef, duplicateIndex, t('attachments.duplicateError'));
      return;
    }
    setImages(newFiles);
    onImagesChange(newFiles);
  }, [findDuplicateFileIndex, onImagesChange, rejectAndRemove, t]);

  const handleFileChange = useCallback((e: any) => {
    const newFiles: AttachmentItem[] = e.target.value || [];
    const maxFileSizeBytes = MAX_ISSUE_FILE_SIZE_MB * 1024 * 1024;
    const oversizedIndex = newFiles.findIndex(f => isFileItem(f) && f.size > maxFileSizeBytes);
    if (oversizedIndex !== -1) {
      rejectAndRemove(fileInputRef, oversizedIndex, t('attachments.sizeError', { max: MAX_ISSUE_FILE_SIZE_MB }));
      return;
    }
    const unsupportedIndex = newFiles.findIndex(f => {
      if (!isFileItem(f)) {
        return false;
      }
      const ext = getExtension(f.name);
      return !ext || !allowedExtensionsNormalized.includes(ext);
    });
    if (unsupportedIndex !== -1) {
      rejectAndRemove(fileInputRef, unsupportedIndex, t('attachments.typeError'));
      return;
    }
    const duplicateIndex = findDuplicateFileIndex(newFiles);
    if (duplicateIndex !== -1) {
      rejectAndRemove(fileInputRef, duplicateIndex, t('attachments.duplicateError'));
      return;
    }
    setFiles(newFiles);
    onFilesChange(newFiles);
  }, [allowedExtensionsNormalized, findDuplicateFileIndex, onFilesChange, rejectAndRemove, t]);

  return (
    <>
      <div onClick={toggleExpand} className={classes['section']}>
        {t('attachments.title')}
        {isExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
      </div>
      {isExpanded && (
        <>
          <div className={classes['attachment-row']}>
            <div className={classes['attachment-label']}>
              <Icon name="photo_camera" />
              <span>{t('attachments.images')} {t('attachments.range', { current: images.length, max: MAX_ISSUE_IMAGES })}</span>
            </div>
            <Button
              className={classes['upload-btn']}
              type="button"
              disabled={images.length === MAX_ISSUE_IMAGES}
              onClick={(e) => imageInputRef.current?.triggerFileInputClick(e)}
            >
              <Icon name="upload" />
              {t('attachments.upload')}
            </Button>
          </div>
          <div className={classes['attachment-hint']}>{t('attachments.imageHint', { max: MAX_ISSUE_IMAGE_SIZE_MB })}</div>

          <File
            ref={imageInputRef}
            name="images"
            maxFiles={MAX_ISSUE_IMAGES}
            preview
            showInput={false}
            accept="image/*"
            defaultValue={defaultImages as any}
            onChange={handleImageChange}
          />

          <hr className={classes['attachment-divider']} />

          <div className={classes['attachment-row']}>
            <div className={classes['attachment-label']}>
              <Icon name="description" />
              <span>{t('attachments.files')} {t('attachments.range', { current: files.length, max: MAX_ISSUE_FILES })}</span>
            </div>
            <Button
              className={classes['upload-btn']}
              type="button"
              disabled={files.length === MAX_ISSUE_FILES || !fileAccept}
              onClick={(e) => fileInputRef.current?.triggerFileInputClick(e)}
            >
              <Icon name="upload" />
              {t('attachments.upload')}
            </Button>
          </div>
          <div className={classes['attachment-hint']}>{t('attachments.fileHint', { max: MAX_ISSUE_FILE_SIZE_MB })}</div>

          <File
            ref={fileInputRef}
            name="files"
            maxFiles={MAX_ISSUE_FILES}
            preview={false}
            showInput={false}
            accept={fileAccept || ''}
            imageEdit={false}
            defaultValue={defaultFiles as any}
            onChange={handleFileChange}
            onlyImage={false}
          />

          {files.length > 0 && (
            <div className={classes['file-preview-list']}>
              {files.map((file, index) => (
                <div key={`${getDisplayName(file)}-${index}`} className={classes['file-preview-item']}>
                  <Icon name="description" className={classes['file-icon']} />
                  <div className={classes['file-info']}>
                    <div className={classes['file-name']}>{getDisplayName(file)}</div>
                    {isFileItem(file) && (
                      <div className={classes['file-size']}>{(file.size / 1024).toFixed(1)} KB</div>
                    )}
                  </div>
                  <Icon
                    name="close"
                    className={classes['file-remove']}
                    onClick={() => fileInputRef.current?.handleImageDelete(index)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Attachments;
